import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { Badge, Button, Card, EmptyState, Field, HeroPill, Input, List, ListItem, MobileHero, MobilePage, SectionHeader, Select, Skeleton, StatCard } from '../../components/mobile/MobilePageKit';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';
const clientName = (result) => `${result.Client?.firstName || ''} ${result.Client?.lastName || ''}`.trim() || 'Client';
const riskLevel = (risk) => risk?.level || 'flagged';

const scoreEntries = (interpretation) => {
  if (!interpretation || typeof interpretation !== 'object') return [];
  return Object.entries(interpretation)
    .filter(([key, value]) => key && !key.startsWith('_') && value && typeof value === 'object' && ('score' in value || 'severity' in value));
};

const parseJsonValue = (value, fallback) => {
  if (typeof value !== 'string') return value ?? fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const questionItems = (questions) => {
  const parsedQuestions = parseJsonValue(questions, []);
  return Array.isArray(parsedQuestions) ? parsedQuestions : [];
};

const detailText = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) return value.map(detailText).filter(Boolean).join('\n');
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const extractDisplayValue = (value) => {
  if (Array.isArray(value)) return value.map(detailText).join(', ');
  if (value && typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'label')) return detailText(value.label);
    if (Object.prototype.hasOwnProperty.call(value, 'value')) return detailText(value.value);
    if (Object.prototype.hasOwnProperty.call(value, 'answer')) return detailText(value.answer);
    if (Object.prototype.hasOwnProperty.call(value, 'response')) return detailText(value.response);
    return detailText(value);
  }
  return value === null || value === undefined ? '—' : detailText(value);
};

const responseValue = (responses, question, index) => {
  const parsedResponses = parseJsonValue(responses, null);
  if (!parsedResponses) return '—';

  const candidates = [question?.id, question?.questionId, question?.question_id, question?.order, index + 1]
    .filter(value => value !== null && value !== undefined && value !== '');

  if (Array.isArray(parsedResponses)) {
    const matchedResponse = parsedResponses.find(response => {
      if (!response || typeof response !== 'object') return false;
      return candidates.some(candidate => [response.questionId, response.question_id, response.id, response.order].some(value => String(value) === String(candidate)));
    });

    if (matchedResponse) return extractDisplayValue(matchedResponse);
    if (parsedResponses[index] !== undefined) return extractDisplayValue(parsedResponses[index]);
    return '—';
  }

  for (const candidate of candidates) {
    if (Object.prototype.hasOwnProperty.call(parsedResponses, candidate)) return extractDisplayValue(parsedResponses[candidate]);
    const stringCandidate = String(candidate);
    if (Object.prototype.hasOwnProperty.call(parsedResponses, stringCandidate)) return extractDisplayValue(parsedResponses[stringCandidate]);
  }

  return '—';
};

const detailItems = (value) => {
  const parsedValue = parseJsonValue(value, value);
  if (Array.isArray(parsedValue)) {
    return parsedValue.map((item, index) => ({ title: `Item ${index + 1}`, text: detailText(item) })).filter(item => item.text);
  }
  if (parsedValue && typeof parsedValue === 'object') {
    return Object.entries(parsedValue).map(([title, text]) => ({ title, text: detailText(text) })).filter(item => item.text);
  }
  const text = detailText(parsedValue);
  return text ? [{ title: '', text }] : [];
};

const MobileFlaggedFollowUps = () => {
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [linkType, setLinkType] = useState('all');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadFlaggedResults = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/practitioner/follow-up-flags`);
      setResults(res.data.results || []);
      setSummary(res.data.summary || null);
    } catch (error) {
      console.error('Mobile follow-up load error:', error);
    } finally {
      setLoading(false);
    }
  };


  const viewDetails = async (result) => {
    setSelected(result);
    setDetailLoading(true);
    try {
      const detailResultId = result.detailResultId || result.id;
      const res = await axios.get(`${API_URL}/practitioner/results/${detailResultId}`);
      setSelected({ ...result, ...res.data, detailResultId, followUpRisk: result.followUpRisk || res.data.followUpRisk });
    } catch (error) {
      console.error('Mobile follow-up detail load error:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => { loadFlaggedResults(); }, []);

  useEffect(() => {
    document.body.classList.toggle('follow-up-detail-open', Boolean(selected));
    return () => document.body.classList.remove('follow-up-detail-open');
  }, [selected]);

  const filteredResults = useMemo(() => {
    const q = search.toLowerCase();
    return results.filter(result => {
      const isOrg = result.TestAssignment?.linkType === 'organization';
      const matchesType = linkType === 'all' || (linkType === 'organization' ? isOrg : !isOrg);
      const haystack = `${clientName(result)} ${result.Client?.email || ''} ${result.TestCatalog?.shortName || ''} ${result.TestAssignment?.organizationName || ''} ${(result.followUpRisk?.reasons || []).join(' ')}`.toLowerCase();
      return matchesType && haystack.includes(q);
    });
  }, [results, search, linkType]);

  return (
    <MobilePage>
      <MobileHero>
        <HeroPill>Priority Review</HeroPill>
        <h2>Follow Ups</h2>
        <p>Track clients flagged for follow-up and review organization or individual link context.</p>
      </MobileHero>

      {loading ? <Skeleton $height="320px" /> : (
        <>
          <StatsGrid>
            <StatCard $accent="#dc2626"><span>Total flagged</span><strong>{summary?.total || results.length}</strong><small>Need review</small></StatCard>
            <StatCard $accent="#f97316"><span>Critical</span><strong>{summary?.critical || 0}</strong><small>Highest risk</small></StatCard>
            <StatCard $accent="#d97706"><span>High</span><strong>{summary?.high || 0}</strong><small>Priority</small></StatCard>
            <StatCard $accent="#2563eb"><span>Organizations</span><strong>{summary?.organizationLinks || 0}</strong><small>Group links</small></StatCard>
            <StatCard $accent="#7c3aed"><span>Individual</span><strong>{summary?.individualLinks || 0}</strong><small>Direct links</small></StatCard>
          </StatsGrid>

          <Card>
            <SectionHeader><div><span>Filters</span><h3>Tracker</h3></div><Button $secondary type="button" onClick={loadFlaggedResults}>Refresh</Button></SectionHeader>
            <FilterStack>
              <Field>Search<Input value={search} onChange={event => setSearch(event.target.value)} placeholder="Client, organization, reason…" /></Field>
              <Field>Link tracking<Select value={linkType} onChange={event => setLinkType(event.target.value)}><option value="all">All flagged clients</option><option value="organization">Organization links</option><option value="individual">Individual links</option></Select></Field>
            </FilterStack>
          </Card>

          {summary?.organizations?.length > 0 && linkType !== 'individual' && (
            <Card>
              <SectionHeader><div><span>Organizations</span><h3>Organization summary</h3></div></SectionHeader>
              <OrgPanel>{summary.organizations.map(org => <OrgCard key={org.organizationName}><h4>{org.organizationName}</h4><div><span>Total flagged</span><strong>{org.total}</strong></div><div><span>Critical</span><strong>{org.critical}</strong></div><div><span>High</span><strong>{org.high}</strong></div><div><span>Latest flag</span><strong>{formatDate(org.latestFlaggedAt)}</strong></div></OrgCard>)}</OrgPanel>
            </Card>
          )}

          <Card>
            <SectionHeader><div><span>Flagged clients</span><h3>{filteredResults.length} case{filteredResults.length === 1 ? '' : 's'}</h3></div></SectionHeader>
            {filteredResults.length === 0 ? <EmptyState><strong>No flagged clients found</strong><p>Try changing the filter or refreshing the tracker.</p></EmptyState> : (
              <List>
                {filteredResults.map(result => {
                  const isOrg = result.TestAssignment?.linkType === 'organization';
                  return (
                    <ListItem key={result.id} as="div">
                      <FlagIcon>🚩</FlagIcon>
                      <FlagCopy>
                        <strong>{clientName(result)}</strong>
                        <span>{result.TestCatalog?.shortName || 'Assessment'} • {isOrg ? result.TestAssignment?.organizationName || 'Organization' : 'Individual link'}</span>
                      </FlagCopy>
                      <Badge $tone={riskLevel(result.followUpRisk)}>{riskLevel(result.followUpRisk)}</Badge>
                      <Button type="button" onClick={() => viewDetails(result)}>View Details</Button>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Card>
        </>
      )}

      {selected && (
        <SheetBackdrop onClick={() => setSelected(null)}>
          <DetailSheet onClick={event => event.stopPropagation()}>
            <Handle />
            <SectionHeader><div><span>Follow-up detail</span><h3>{clientName(selected)}</h3></div><Button $secondary type="button" onClick={() => setSelected(null)}>Close</Button></SectionHeader>
            <DetailRow><span>Risk</span><Badge $tone={riskLevel(selected.followUpRisk)}>{riskLevel(selected.followUpRisk)}</Badge></DetailRow>
            <DetailRow><span>Test</span><strong>{selected.TestCatalog?.shortName || selected.TestCatalog?.name || 'Assessment'}</strong></DetailRow>
            <DetailRow><span>Link tracking</span><strong>{selected.TestAssignment?.linkType === 'organization' ? selected.TestAssignment?.organizationName || 'Organization' : 'Direct client link'}</strong></DetailRow>
            <DetailRow><span>Email</span><strong>{selected.Client?.email || selected.TestAssignment?.clientEmail || 'No email'}</strong></DetailRow>
            <DetailRow><span>Date</span><strong>{formatDate(selected.completedAt)}</strong></DetailRow>
            <ReasonList>{(selected.followUpRisk?.reasons || [selected.followUpRisk?.recommendedAction || 'Review client follow-up.']).map((reason, index) => <li key={index}>{reason}</li>)}</ReasonList>
            {detailLoading ? <LoadingText>Loading full result details…</LoadingText> : (
              <>
                <SectionTitle>Client details</SectionTitle>
                <DetailRow><span>Age</span><strong>{selected.Client?.age || '—'}</strong></DetailRow>
                <DetailRow><span>Gender</span><strong>{selected.Client?.genderCustom || selected.Client?.gender || '—'}</strong></DetailRow>
                <DetailRow><span>Phone</span><strong>{selected.Client?.phone || '—'}</strong></DetailRow>
                <DetailRow><span>Occupation</span><strong>{selected.Client?.occupation || '—'}</strong></DetailRow>
                {scoreEntries(selected.interpretation).length > 0 && (
                  <>
                    <SectionTitle>Result scores</SectionTitle>
                    <ScoreStack>
                      {scoreEntries(selected.interpretation).map(([key, value]) => (
                        <ScoreChip key={key}><span>{key}</span><strong>{value.score ?? '—'}</strong><small>{value.severity || 'No severity'}</small></ScoreChip>
                      ))}
                    </ScoreStack>
                  </>
                )}
                {detailItems(selected.interpretation?._description).length > 0 && <InfoBox><strong>Interpretation</strong><p>{detailItems(selected.interpretation?._description).map(item => item.text).join('\n\n')}</p></InfoBox>}
                {detailItems(selected.interpretation?._recommendations).length > 0 && (
                  <>
                    <SectionTitle>Recommendations</SectionTitle>
                    <RecommendationStack>
                      {detailItems(selected.interpretation?._recommendations).map((item, index) => (
                        <RecommendationCard key={`${item.title}-${index}`}>
                          {item.title && <strong>{item.title}</strong>}
                          <p>{item.text}</p>
                        </RecommendationCard>
                      ))}
                    </RecommendationStack>
                  </>
                )}
                {questionItems(selected.TestCatalog?.questions).length > 0 && (
                  <>
                    <SectionTitle>Responses</SectionTitle>
                    <ResponseStack>
                      {questionItems(selected.TestCatalog?.questions).map((question, index) => <ResponseChip key={question.id || index}><span>{index + 1}. {detailText(question.text) || 'Question'}</span><strong>{responseValue(selected.responses, question, index)}</strong></ResponseChip>)}
                    </ResponseStack>
                  </>
                )}
              </>
            )}
          </DetailSheet>
        </SheetBackdrop>
      )}
    </MobilePage>
  );
};

const StatsGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const FilterStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OrgPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OrgCard = styled.article`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 12px;
  h4 { color: #0f172a; margin: 0 0 10px; }
  div { align-items: center; display: flex; justify-content: space-between; padding: 5px 0; }
  span { color: #64748b; font-size: 0.76rem; font-weight: 850; }
  strong { color: #0f172a; font-size: 0.82rem; }
`;

const FlagIcon = styled.div`
  align-items: center;
  background: #fee2e2;
  border-radius: 17px;
  display: flex;
  height: 46px;
  justify-content: center;
  width: 46px;
`;

const FlagCopy = styled.div`
  flex: 1;
  min-width: 0;
  strong { color: #0f172a; display: block; font-size: 0.9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  span { color: #64748b; display: block; font-size: 0.74rem; margin-top: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const SheetBackdrop = styled.div`
  background: rgba(15, 23, 42, 0.36);
  inset: 0;
  position: fixed;
  z-index: 90;
`;

const DetailSheet = styled.section`
  background: white;
  border-radius: 30px 30px 0 0;
  bottom: 0;
  left: 0;
  max-height: calc(88vh - env(safe-area-inset-top));
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 10px 16px calc(22px + env(safe-area-inset-bottom));
  position: fixed;
  right: 0;
  -webkit-overflow-scrolling: touch;
`;

const Handle = styled.div`
  background: #cbd5e1;
  border-radius: 99px;
  height: 5px;
  margin: 0 auto 15px;
  width: 46px;
`;

const DetailRow = styled.div`
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 9px;
  padding: 12px;
  span { color: #64748b; font-size: 0.76rem; font-weight: 850; }
  strong { color: #0f172a; font-size: 0.86rem; text-align: right; }
`;

const ReasonList = styled.ul`
  background: #fff7ed;
  border-radius: 16px;
  color: #7c2d12;
  font-size: 0.82rem;
  line-height: 1.45;
  margin: 0;
  padding: 14px 14px 14px 30px;
`;


const LoadingText = styled.div`
  color: #64748b;
  font-size: 0.84rem;
  font-weight: 850;
  padding: 12px 2px;
`;

const SectionTitle = styled.h4`
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 950;
  margin: 16px 0 9px;
`;

const ScoreStack = styled.div`
  display: grid;
  gap: 8px;
`;

const ScoreChip = styled.div`
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 16px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 10px;
  padding: 12px;
  span { color: #9a3412; font-size: 0.78rem; font-weight: 900; }
  strong { color: #0f172a; font-size: 1rem; }
  small { color: #b45309; font-weight: 850; grid-column: 1 / -1; }
`;

const RecommendationStack = styled.div`
  display: grid;
  gap: 8px;
`;

const RecommendationCard = styled.div`
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 16px;
  padding: 12px;

  strong { color: #9a3412; display: block; font-size: 0.8rem; margin-bottom: 5px; }
  p { color: #334155; font-size: 0.82rem; line-height: 1.45; margin: 0; white-space: pre-line; }
`;

const InfoBox = styled.div`
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  border-radius: 16px;
  color: #475569;
  line-height: 1.45;
  margin-top: 10px;
  padding: 12px;
  strong { color: #0f172a; display: block; margin-bottom: 4px; }
  p { margin: 0; }
`;

const ResponseStack = styled.div`
  display: grid;
  gap: 8px;
`;

const ResponseChip = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  display: grid;
  gap: 6px;
  padding: 10px;
  span { color: #334155; font-size: 0.8rem; line-height: 1.35; }
  strong { color: #0f172a; font-size: 0.84rem; }
`;

export default MobileFlaggedFollowUps;
